import { useEffect, useState } from "react";
import { toast } from "sonner";
import { DatabaseIcon as Database } from "@phosphor-icons/react";

import {
  getFindingOccurrences,
  getFindingsOverview,
  getFindingsPatterns,
  getFindingsRepeatedNames,
} from "@/lib/api";
import FindingsOverviewStrip from "@/components/findings/FindingsOverviewStrip";
import RepeatedNamesTable from "@/components/findings/RepeatedNamesTable";
import PatternRecommendations from "@/components/findings/PatternRecommendations";
import OccurrenceDetail from "@/components/findings/OccurrenceDetail";
import ValidatedRulesPanel from "@/components/findings/ValidatedRulesPanel";
import { CompareSelectionBar } from "@/components/compare/CompareSelectionBar";
import { useCompareSelection } from "@/hooks/useCompareSelection";

export default function Findings() {
  const compareSelection = useCompareSelection(); // Iter 63 — pick two objects from the drill-down / rules tables
  // Top-level state. Kept flat (no reducer) — the page has ~4 widgets and
  // there's nothing complex about how they relate.
  const [overview, setOverview] = useState(null);
  const [repeated, setRepeated] = useState([]);
  const [patterns, setPatterns] = useState([]);
  const [loadingTop, setLoadingTop] = useState(true);

  // Drill-down state for the right column.
  const [picked, setPicked] = useState(null);
  const [pickedData, setPickedData] = useState(null);
  const [pickedLoading, setPickedLoading] = useState(false);
  const [pickedError, setPickedError] = useState(null);

  useEffect(() => {
    let alive = true;
    setLoadingTop(true);
    // Per-call .catch so a single endpoint failure doesn't blank every panel.
    // Each section will independently fall back to "no data" rather than the
    // whole page going empty.
    const warn = (section) => (e) => {
      if (!alive) return [];
      toast.error(`Couldn't load ${section}: ${e?.response?.data?.detail || e.message}`);
      return null;
    };
    Promise.all([
      getFindingsOverview().catch(warn("overview")),
      getFindingsRepeatedNames({ minWorkspaces: 2, limit: 100 }).catch(
        warn("recurring file names"),
      ),
      getFindingsPatterns().catch(warn("playbook patterns")),
    ])
      .then(([ov, rn, pt]) => {
        if (!alive) return;
        if (ov) setOverview(ov);
        if (rn) setRepeated(rn.rows || []);
        if (pt) setPatterns(pt.rows || []);
      })
      .finally(() => alive && setLoadingTop(false));
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    if (!picked) {
      setPickedData(null);
      setPickedError(null);
      return undefined;
    }
    let alive = true;
    setPickedLoading(true);
    setPickedError(null);
    getFindingOccurrences(picked)
      .then((d) => alive && setPickedData(d))
      .catch((e) =>
        alive &&
        setPickedError(e?.response?.data?.detail || "Could not load occurrences."),
      )
      .finally(() => alive && setPickedLoading(false));
    return () => {
      alive = false;
    };
  }, [picked]);

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-8" data-testid="findings-page">
      <header className="mb-6">
        <div className="flex items-center gap-1.5 ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] mb-1">
          <Database size={11} weight="bold" />
          Knowledge base
        </div>
        <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight">
          Findings across your engagements
        </h1>
        <p className="text-[var(--ml-text-secondary)] mt-2 max-w-2xl">
          Spot recurring patterns that span clients. Files that appear again and
          again — and the playbooks that keep working.
        </p>
      </header>

      <FindingsOverviewStrip overview={overview} />

      <div className="mb-4">
        <CompareSelectionBar selection={compareSelection} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          <RepeatedNamesTable
            rows={repeated}
            loading={loadingTop}
            onPick={setPicked}
            selected={picked}
          />
          <OccurrenceDetail
            fileName={picked}
            data={pickedData}
            loading={pickedLoading}
            error={pickedError}
            compareSelection={compareSelection}
          />
        </div>
        <div>
          <PatternRecommendations rows={patterns} loading={loadingTop} />
        </div>
      </div>

      <div className="mt-4">
        <ValidatedRulesPanel compareSelection={compareSelection} />
      </div>
    </div>
  );
}
